import { ArrowRight, CheckCircle2, Star, Users, Zap, Shield, Gift, ChevronRight } from "lucide-react";
import { useParams, Link } from "react-router-dom";
import { useEffect } from "react";

import { useTranslation } from 'react-i18next';
const TESTIMONIALS = [
  {
    name: "Alex Chen",
    role: "CTO at TechFlow",
    text: "Max Employee transformed how we manage our team. The AI automation saves us 20+ hours per week.",
    rating: 5,
  },
  {
    name: "Sarah Kim",
    role: "Head of Ops at ScaleUp",
    text: "Best workforce management platform we've used. The referral program paid for our first 3 months!",
    rating: 5,
  },
  {
    name: "Marcos Silva",
    role: "Founder at DevHouse",
    text: "From onboarding to payroll, Max Employee handles it all. The $25 referral credit was a nice bonus.",
    rating: 5,
  },
];

const FEATURES = [
  { icon: Zap, title: "AI-Powered Automation", desc: "Automate repetitive tasks with smart AI workflows" },
  { icon: Users, title: "Team Management", desc: "Manage your entire team from one unified dashboard" },
  { icon: Shield, title: "Enterprise Security", desc: "SOC 2 compliant with end-to-end encryption" },
  { icon: Gift, title: "Referral Rewards", desc: "Earn $25 for every friend who joins" },
];

export default function ReferralLanding() {
  const { t } = useTranslation();
  const { code } = useParams();

  useEffect(() => {
    // Auto-apply referral code to registration flow
    if (code) {
      sessionStorage.setItem("referral_code", code);
      sessionStorage.setItem("referral_source", "landing");
    }
  }, [code]);

  return (
    <div className="min-h-screen bg-zinc-950">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-zinc-800">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 via-transparent to-violet-500/5" />
        <div className="relative max-w-5xl mx-auto px-4 py-20 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-amber-500/10 border border-amber-500/20 mb-6">
            <Gift className="w-4 h-4 text-amber-400" />
            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-amber-400">{t('referral_landing.invited_you')}</span>
          </div>

          <h1 className="font-display text-4xl md:text-6xl text-white mb-6 leading-tight">
            Your Friend Invited You to
            <br />
            <span className="text-amber-400">{t('referral_landing.max_employee')}</span>
          </h1>

          <p className="font-mono text-[15px] text-zinc-400 max-w-2xl mx-auto leading-relaxed mb-8">
            The AI-powered employee management platform used by 10,000+ teams.
            Sign up today and get <span className="text-amber-400 font-bold">$25 in free credits</span>.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/signup"
              state={{ referralCode: code }}
              className="inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-amber-500 text-black font-mono text-sm uppercase tracking-wider hover:bg-amber-400 transition-colors"
            >
              Get Started — $25 Free
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/"
              className="inline-flex items-center justify-center gap-2 px-8 py-3.5 border border-zinc-700 hover:border-zinc-500 font-mono text-sm text-zinc-300 transition-colors"
            >
              Learn More
            </Link>
          </div>

          <p className="font-mono text-[10px] text-zinc-600 mt-4">{t('referral_landing.free_trial_no_credit_card_required')}</p>
        </div>
      </section>

      {/* Social Proof Stats */}
      <section className="border-b border-zinc-800 bg-zinc-950/50">
        <div className="max-w-4xl mx-auto px-4 py-10">
          <div className="grid grid-cols-3 gap-6 text-center">
            <div>
              <div className="font-display text-2xl md:text-3xl text-amber-400">10K+</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-1">{t('referral_landing.active_teams')}</div>
            </div>
            <div>
              <div className="font-display text-2xl md:text-3xl text-white">$2.5M+</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-1">{t('referral_landing.credits_earned_global')}</div>
            </div>
            <div>
              <div className="font-display text-2xl md:text-3xl text-white">99.9%</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-1">{t('referral_landing.uptime_sla')}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="border-b border-zinc-800">
        <div className="max-w-5xl mx-auto px-4 py-16">
          <div className="text-center mb-10">
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500">{t('referral_landing.features')}</span>
            <h2 className="font-display text-2xl text-white mt-2">{t('referral_landing.everything_you_need')}</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {FEATURES.map((f) => (
              <div key={f.title} className="border border-zinc-800 bg-zinc-950/30 p-6 flex gap-4 hover:border-zinc-700 transition-colors">
                <div className="w-10 h-10 bg-amber-500/10 border border-amber-500/20 flex items-center justify-center shrink-0">
                  <f.icon className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <h3 className="font-display text-lg text-white mb-1">{f.title}</h3>
                  <p className="font-mono text-[12px] text-zinc-500 leading-relaxed">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="border-b border-zinc-800">
        <div className="max-w-5xl mx-auto px-4 py-16">
          <div className="text-center mb-10">
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500">{t('referral_landing.testimonials')}</span>
            <h2 className="font-display text-2xl text-white mt-2">{t('referral_landing.loved_by_teams')}</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="border border-zinc-800 bg-zinc-950/30 p-6">
                <div className="flex gap-0.5 mb-3">
                  {Array.from({ length: t.rating }).map((_, j) => (
                    <Star key={j} className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
                  ))}
                </div>
                <p className="font-mono text-[12px] text-zinc-400 leading-relaxed mb-4">"{t.text}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center">
                    <span className="font-mono text-[9px] text-zinc-400">{t.name[0]}</span>
                  </div>
                  <div>
                    <div className="font-mono text-[11px] text-white">{t.name}</div>
                    <div className="font-mono text-[9px] text-zinc-600">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-t from-amber-500/5 to-transparent" />
        <div className="relative max-w-2xl mx-auto px-4 py-20 text-center">
          <Gift className="w-10 h-10 text-amber-400 mx-auto mb-4" />
          <h2 className="font-display text-3xl text-white mb-3">
            Claim Your $25 Credit
          </h2>
          <p className="font-mono text-[13px] text-zinc-500 mb-6">
            Your friend's referral gives you $25 in free credits. Start your free trial now.
          </p>
          <Link
            to="/signup"
            state={{ referralCode: code }}
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-amber-500 text-black font-mono text-sm uppercase tracking-wider hover:bg-amber-400 transition-colors"
          >
            Start Free Trial
            <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-zinc-800 py-6">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="font-mono text-[10px] text-zinc-600">
            Max Employee — AI-Powered Team Management
          </p>
        </div>
      </footer>
    </div>
  );
}
